import { Link } from "react-router-dom";
import { Building, Layers, Network, Check, ArrowRight } from "lucide-react";
import useSEO from "@/components/site/useSEO";
import PageHero from "@/components/site/PageHero";
import { SectionHead, CTABand } from "@/components/site/ui";
import { Reveal, Stagger, StaggerItem } from "@/components/site/Reveal";

const BANDS = [
  {
    icon: Building,
    name: "Single service",
    for: "For a single care service getting started with connected care management.",
    includes: ["Core care delivery & medication", "Staff rota & attendance", "Governance essentials", "AYOBELL readiness indicators"],
  },
  {
    icon: Layers,
    name: "Multi-site",
    for: "For providers running several services or houses that need shared oversight.",
    includes: ["Everything in Single service", "Multi-site management & filtering", "Payroll & Staff Hours", "Manager dashboards & reporting"],
    featured: true,
  },
  {
    icon: Network,
    name: "Group / large",
    for: "For larger organisations and groups with more complex requirements.",
    includes: ["Everything in Multi-site", "Data Migration Centre support", "Governance & audit at scale", "Tailored onboarding"],
  },
];

export default function Pricing() {
  useSEO(
    "Pricing | AYOBELL Care Management Platform",
    "AYOBELL pricing grows with your service and is based on your organisation size and requirements. Talk to our team or request pricing tailored to your service."
  );
  return (
    <>
      <PageHero
        eyebrow="Pricing"
        title="Simple pricing that grows with your service."
        subtitle="AYOBELL pricing is based on your organisation size and service requirements. Tell us about your organisation and we'll build a package that fits."
        primary={{ to: "/contact", label: "Request Pricing" }}
        secondary={{ to: "/contact", label: "Talk to our team" }}
      />

      <section className="bg-white py-20 sm:py-28 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <SectionHead center eyebrow="How pricing works" title="Positioned around your service, not a fixed list" desc="Choose the shape that best matches your organisation — we'll tailor the details with you." />
          <Stagger className="grid md:grid-cols-3 gap-6 mt-14 items-stretch">
            {BANDS.map((b) => (
              <StaggerItem key={b.name}>
                <div className={`h-full rounded-3xl border p-8 flex flex-col transition-all duration-300 ${b.featured ? "bg-slate-900 border-slate-800 text-white shadow-xl md:-translate-y-2" : "bg-white border-slate-200 hover:shadow-xl"}`}>
                  {b.featured && <span className="self-start text-[10px] font-bold uppercase tracking-wider bg-emerald-400 text-slate-900 px-2 py-1 rounded-full mb-4">Most common</span>}
                  <span className={`flex items-center justify-center w-12 h-12 rounded-xl ${b.featured ? "bg-white/10 text-teal-300" : "bg-teal-50 text-teal-600"}`}><b.icon className="w-5 h-5" /></span>
                  <h3 className={`font-display font-bold text-xl mt-4 ${b.featured ? "text-white" : "text-slate-900"}`}>{b.name}</h3>
                  <p className={`mt-2 text-sm leading-relaxed ${b.featured ? "text-slate-300" : "text-slate-600"}`}>{b.for}</p>
                  <div className={`my-6 h-px ${b.featured ? "bg-white/10" : "bg-slate-100"}`} />
                  <ul className="space-y-2.5 flex-1">
                    {b.includes.map((it) => (
                      <li key={it} className={`flex items-start gap-2 text-sm ${b.featured ? "text-slate-300" : "text-slate-600"}`}>
                        <Check className={`w-4 h-4 mt-0.5 shrink-0 ${b.featured ? "text-emerald-400" : "text-teal-500"}`} />{it}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/contact"
                    data-testid={`pricing-band-${b.name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-+$/, "")}`}
                    className={`mt-8 inline-flex items-center justify-center gap-2 text-sm font-semibold px-6 py-3.5 rounded-full transition-all ${b.featured ? "bg-gradient-to-r from-teal-500 to-emerald-500 text-white hover:shadow-lg hover:shadow-emerald-500/30" : "bg-slate-100 text-slate-800 hover:bg-slate-200"}`}
                  >
                    Request Pricing <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal className="mt-10 text-center">
            <p className="text-sm text-slate-500 max-w-2xl mx-auto">Final commercial packages are agreed with you directly based on your service. We'll never surprise you with a fixed tier that doesn't fit.</p>
          </Reveal>
        </div>
      </section>

      <CTABand title="Let's build pricing around your organisation." desc="Talk to our team and we'll tailor AYOBELL to your service, teams and requirements." primaryLabel="Request Pricing" primaryTo="/contact" secondaryLabel="Talk to our team" secondaryTo="/contact" />
    </>
  );
}
